import * as rrwebModule from "rrweb"

export interface NativeReplayRecorder {
  capture: (path: string) => void
  flush: () => void
  stop: () => void
}

export interface ReplayFrameEvent {
  type: number
  timestamp: number
  data: {
    tag: string
    image: string
    path: string
  }
}

function getViewShot(): { captureScreen: (opts: any) => Promise<string> } | null {
  try {
    const req = new Function("m", "try { return require(m) } catch (e) { return null }") as (
      m: string,
    ) => any
    const mod = req("react-native-view-shot")
    if (mod && typeof mod.captureScreen === "function") return mod
    if (mod?.default && typeof mod.default.captureScreen === "function") return mod.default
    return null
  } catch {
    return null
  }
}

// Resilient resolver for rrweb record function (Expo Web)
function getRecordFn() {
  const mod = rrwebModule as any
  if (typeof mod.record === "function") return mod.record
  if (typeof mod.default?.record === "function") return mod.default.record
  if (typeof mod.default === "function") return mod.default
  if (typeof (globalThis as any)?.rrweb?.record === "function") return (globalThis as any).rrweb.record
  return null
}

export function startNativeReplay(
  sink: (events: unknown[]) => void,
  getCurrentScreen: () => string = () => "/",
): NativeReplayRecorder | null {
  // 1. If running on Web (Expo Web / React Native Web), use rrweb for full DOM capture
  const isWeb =
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    typeof document.createElement === "function"

  if (isWeb) {
    const record = getRecordFn()
    if (record) {
      let queue: unknown[] = []
      const flushQueue = () => {
        if (queue.length > 0) {
          const batch = queue.slice()
          queue = []
          sink(batch)
        }
      }

      let stopFn: (() => void) | null = null
      try {
        stopFn = record({
          emit(event: any, isCheckout?: boolean) {
            queue.push(event)
            if (isCheckout || event?.type === 2) {
              setTimeout(flushQueue, 100)
            }
          },
          checkoutEveryNms: 60_000,
          inlineStylesheet: true,
        })
      } catch {
        stopFn = null
      }

      if (stopFn) {
        const interval = setInterval(flushQueue, 2500)
        return {
          capture: () => flushQueue(),
          flush: flushQueue,
          stop: () => {
            clearInterval(interval)
            flushQueue()
            try {
              stopFn?.()
            } catch {}
          },
        }
      }
    }
  }

  // 2. If running on Native (iOS/Android via React Native / Expo), use screenshot frame capture
  const viewShot = getViewShot()
  if (!viewShot) {
    return null
  }

  let stopped = false
  let capturing = false
  let lastCaptureTime = 0
  const MIN_CAPTURE_INTERVAL_MS = 1200

  const doCapture = async (screenPath?: string) => {
    if (stopped || capturing) return
    const now = Date.now()
    if (now - lastCaptureTime < MIN_CAPTURE_INTERVAL_MS) return

    capturing = true
    lastCaptureTime = now

    try {
      const base64 = await viewShot.captureScreen({
        format: "jpg",
        quality: 0.4,
        result: "base64",
      })

      if (base64 && !stopped) {
        const frameEvent: ReplayFrameEvent = {
          type: 5, // rrweb custom event
          timestamp: now,
          data: {
            tag: "relia-screen-frame",
            image: `data:image/jpeg;base64,${base64}`,
            path: screenPath || getCurrentScreen() || "/",
          },
        }
        sink([frameEvent])
      }
    } catch {
      // Screen capture failed (e.g. secure screen, backgrounded, or unmounted view)
    } finally {
      capturing = false
    }
  }

  // Initial screenshot after 600ms once initial UI tree renders
  const initialTimer = setTimeout(() => {
    void doCapture()
  }, 600)

  // Periodic capture every 3.5s to track visual changes
  const periodicTimer = setInterval(() => {
    void doCapture()
  }, 3500)

  return {
    capture: (path: string) => {
      // Immediate capture on screen change or touch (debounced)
      setTimeout(() => void doCapture(path), 150)
    },
    flush: () => {
      void doCapture()
    },
    stop: () => {
      stopped = true
      clearTimeout(initialTimer)
      clearInterval(periodicTimer)
    },
  }
}
